import { format, parseISO } from 'date-fns';
import i18n from '@/i18n';

const PLATFORM_LABEL_KEYS = {
  '1688': '',
  '1688.com': '',
  taobao: 'platformTaobao',
  tmall: 'platformTmall',
  jd: 'platformJd',
  'jd.com': 'platformJd',
  '3.cn': 'platformJd',
};

/**
 * 插件采集批次：平台展示名
 * @param {string} [platformKey]
 */
export function formatSourcingPlatformLabel(platformKey, t) {
  const pk = String(platformKey || '').trim().toLowerCase();
  const unknown = t ? t('sourcingHub.unknownPlatform') : '';
  const localized = (key, fallback) => (t ? t(`sourcingHub.${key}`) : fallback);
  if (!pk) return unknown;
  if (Object.prototype.hasOwnProperty.call(PLATFORM_LABEL_KEYS, pk)) {
    const key = PLATFORM_LABEL_KEYS[pk];
    return key ? localized(key, pk) : '1688';
  }
  if (pk.includes('1688')) return '1688';
  if (pk.includes('tmall')) return localized('platformTmall', 'Tmall');
  if (pk.includes('taobao')) return localized('platformTaobao', 'Taobao');
  if (pk.includes('jd')) return localized('platformJd', 'JD');
  return String(platformKey).trim() || unknown;
}

/**
 * @param {string} [iso]
 * @returns {{ dateTimeLabel: string, weekdayLabel: string }}
 */
export function formatSourcingBatchDateTime(iso) {
  if (!iso) return { dateTimeLabel: '', weekdayLabel: '' };
  try {
    const d = parseISO(String(iso).replace(' ', 'T'));
    const lang = i18n.resolvedLanguage || i18n.language || 'zh-CN';
    return {
      dateTimeLabel: format(d, 'yyyy-MM-dd HH:mm'),
      weekdayLabel: new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(d),
    };
  } catch {
    return { dateTimeLabel: '', weekdayLabel: '' };
  }
}

/**
 * 批次下拉选项文案：时间（星期）· 平台 · 条数
 * @param {{ batchKey?: string, platformKey?: string, sourceSite?: string, itemCount?: number, createdAt?: string }} batch
 */
export function formatSourcingBatchOptionLabel(batch, t) {
  if (!batch) return '';
  const platform = formatSourcingPlatformLabel(batch.platformKey || batch.sourceSite, t);
  const count =
    batch.itemCount != null && batch.itemCount !== ''
      ? (t ? t('sourcingHub.batchItemCount', { count: batch.itemCount }) : String(batch.itemCount))
      : '';
  const { dateTimeLabel, weekdayLabel } = formatSourcingBatchDateTime(batch.createdAt);
  if (dateTimeLabel) {
    const when = weekdayLabel ? `${dateTimeLabel}（${weekdayLabel}）` : dateTimeLabel;
    return `${when} · ${platform}${count ? ` · ${count}` : ''}`;
  }
  const bk = batch.batchKey ? String(batch.batchKey).slice(0, 12) : '';
  return `${platform}${count ? ` · ${count}` : ''}${bk ? ` · ${bk}…` : ''}`;
}

/** 截取 URL 的域名前缀，如 https://detail.1688.com/offer/xxx → https://detail.1688.com */
export function getUrlOriginPrefix(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
    return parsed.origin;
  } catch {
    const match = raw.match(/^(https?:\/\/[^/?#]+)/i);
    return match ? match[1] : raw;
  }
}

/**
 * 批次网址列：按域名前缀去重，仅展示 origin。
 * @returns {Array<{ href: string, label: string }>}
 */
export function getBatchDisplaySourceUrls(sourceUrls, pageUrl) {
  const candidates = [];
  if (Array.isArray(sourceUrls)) {
    for (const url of sourceUrls) {
      const trimmed = String(url || '').trim();
      if (trimmed) candidates.push(trimmed);
    }
  }
  const fallback = String(pageUrl || '').trim();
  if (candidates.length === 0 && fallback) candidates.push(fallback);

  const seen = new Set();
  const out = [];
  for (const url of candidates) {
    const origin = getUrlOriginPrefix(url);
    if (!origin || seen.has(origin)) continue;
    seen.add(origin);
    out.push({ href: origin, label: origin });
  }
  return out;
}
